<script lang="ts" module>
  import type { HTMLButtonAttributes } from 'svelte/elements';
  import { Tooltip } from 'bits-ui';
  import type { Snippet } from 'svelte';

  import { tvt } from '@/ui/tv';

  export interface ToolTipProps extends HTMLButtonAttributes {
    trigger?: Snippet<[Record<string, unknown>]>;
    children?: Snippet;
    active?: boolean;
    anchor?: 'top' | 'bottom' | 'left' | 'right';
    size?: 'small' | 'big';

    open?: boolean;
    tooltipClass?: string;

    class?: string;
  }
</script>

<script lang="ts">
  let {
    active,
    anchor = 'top',
    size = 'big',
    open,
    children,
    tooltipClass: tooltipClassName,
    class: className,
    label,
    trigger,
    ..._rest
  }: ToolTipProps & { label: string } = $props();

  const containerStyles = tvt({
    base: 'm-0 rounded-sm text-c-base',
    variants: {
      active: {
        true: 'bg-tooltip-S',
        false: 'bg-tooltip',
      },
      size: {
        small: 'p-1 text-xs',
        big: 'p-2 text-sm',
      },
    },
  });
  const arrowStyles = tvt({
    base: 'size-2',
    variants: {
      active: {
        true: 'text-c-tooltip-S',
        false: 'text-c-tooltip',
      },
    },
  });

  let containerClass = $derived(containerStyles({ active, size, class: tooltipClassName }));
  let arrowClass = $derived(arrowStyles({ active }));
</script>

<!--
@component ToolTip

The ToolTip will render a button as a trigger around the children. If you want to use your own button, you should use a snippet named `trigger`
The tooltip content (the "flyout") is passed as a property name `label`.
If we need more complex content, we could implement a `label` snippet.

use it like this:
<ToolTipProvider>
<ToolTip label="This is a tooltip">
{#snippet trigger({ props })}
  <button {...props}>Click me</button>
{/snippet}
</ToolTip>
</ToolTipProvider>

-->
<!-- 
{#if trigger}
  {@render trigger?.(tooltip.trigger)}
{:else}
  <button {...props} class={className} {...tooltip.trigger}>
    {@render children?.()}
  </button>
{/if}

<div {...tooltip.content} class={containerClass}>
  <div {...tooltip.arrow} class={arrowClass}></div>
</div> -->

<Tooltip.Provider>
  <ul>
    <Tooltip.Root delayDuration={200}>
      {#if trigger}
        <Tooltip.Trigger class={className}>
          {#snippet child({ props })}
            {@render trigger(props)}
          {/snippet}
        </Tooltip.Trigger>
      {:else}
        <Tooltip.Trigger class={className}>
          {@render children?.()}
        </Tooltip.Trigger>
      {/if}
      <Tooltip.Portal>
        <Tooltip.Content sideOffset={4} side={anchor} class="z-40">
          <Tooltip.Arrow class={arrowClass} />
          <div class={containerClass}>
            <p>{label}</p>
          </div>
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  </ul>
</Tooltip.Provider>
